import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    LinearProgress,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import {
    deleteValidation,
    getValidationLogs,
    getValidationStatus,
    listValidationExperiments,
    listValidationJobs,
    pauseValidation,
    resumeValidation,
    startValidationGlobal,
    validateAnalysis,
    validateOne,
} from '../../../infra/api/ValidationPipelineApi';

const STATUS_MAP = {
    completed: { label: 'Concluído', color: 'success' },
    running: { label: 'Executando', color: 'warning' },
    pauseRequested: { label: 'Pausa solicitada', color: 'warning' },
    paused: { label: 'Pausado', color: 'info' },
    error: { label: 'Erro', color: 'error' },
    em_processamento: { label: 'Executando', color: 'warning' },
    concluido: { label: 'Concluído', color: 'success' },
    erro: { label: 'Erro', color: 'error' },
};

function pick(obj, ...keys) {
    for (const k of keys) {
        if (obj?.[k] !== undefined && obj?.[k] !== null) return obj[k];
    }
    return undefined;
}

function parseDateSafe(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}

function fmtDateTime(iso) {
    const d = parseDateSafe(iso);
    if (!d) return '—';
    return d.toLocaleString('pt-BR');
}

function currentTenant() {
    const [, maybe] = window.location.pathname.split('/');
    return maybe || null;
}

function graphPathForExperiment(experimentId) {
    const tenant = currentTenant();
    if (!tenant || !experimentId || experimentId === '—') return '';
    return `/${tenant}/graficos/${encodeURIComponent(experimentId)}`;
}

function fallbackExperimentForGraph(row) {
    return {
        id: row?.experimentId,
        serialNumber: '—',
        protocolName: 'Protocolo não informado',
        analysisName: row?.analysisId && row.analysisId !== '—' ? row.analysisId : 'Análise não informada',
        startDate: null,
        endDate: null,
        status: 'finished',
        protocolColor: '#607d8b',
    };
}

function wsBaseUrl() {
    const httpBase = process.env.REACT_APP_DUMP_PIPE_URL ?? 'http://localhost:8085';
    const parsed = new URL(httpBase);
    parsed.protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
    parsed.pathname = '';
    return parsed.toString().replace(/\/$/, '');
}

function StatBox({ label, value, color }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120, textAlign: 'center', borderColor: color || 'divider' }}>
            <Typography variant="caption" color={color || 'text.secondary'} display="block">
                {label}
            </Typography>
            <Typography variant="h6" fontWeight={700} color={color || 'text.primary'}>
                {value ?? '—'}
            </Typography>
        </Paper>
    );
}

export default function TabValidation() {
    const navigate = useNavigate();
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [status, setStatus] = useState({ hasValidation: false, hasActive: false });
    const compatibilityMode = status?.compatibilityMode || '';
    const [jobs, setJobs] = useState([]);
    const [logs, setLogs] = useState([]);
    const [rows, setRows] = useState([]);

    const [globalLoading, setGlobalLoading] = useState(false);
    const [jobActionLoading, setJobActionLoading] = useState(false);
    const [deleteLoadingId, setDeleteLoadingId] = useState('');

    const [oneId, setOneId] = useState('');
    const [oneLoading, setOneLoading] = useState(false);

    const [analysisInput, setAnalysisInput] = useState('');
    const [analysisMode, setAnalysisMode] = useState('analysisId');
    const [analysisLoading, setAnalysisLoading] = useState(false);

    const [filterValido, setFilterValido] = useState('todos');
    const [filterAnalysis, setFilterAnalysis] = useState('');
    const [tablePage, setTablePage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const wsRef = useRef(null);

    const activeJob = useMemo(
        () => jobs.find((j) => j.status === 'running' || j.status === 'pauseRequested' || j.status === 'paused' || j.status === 'em_processamento'),
        [jobs],
    );

    const progressPct = activeJob?.total > 0
        ? Math.min(100, Math.round(((activeJob.processados || 0) / activeJob.total) * 100))
        : 0;

    const totalRows = rows.length;
    const totalValidos = useMemo(() => rows.filter((r) => r.valido).length, [rows]);
    const totalInvalidos = totalRows - totalValidos;
    const pagedRows = useMemo(() => {
        const start = tablePage * rowsPerPage;
        return rows.slice(start, start + rowsPerPage);
    }, [rows, tablePage, rowsPerPage]);

    const normalizeJob = (j) => ({
        id: pick(j, 'id', '_id', 'ID') || '',
        status: pick(j, 'status', 'Status') || 'completed',
        total: Number(pick(j, 'total', 'Total') || 0),
        processados: Number(pick(j, 'processados', 'Processados') || 0),
        validos: Number(pick(j, 'validos', 'Validos') || 0),
        invalidos: Number(pick(j, 'invalidos', 'Invalidos') || 0),
        createdAt: pick(j, 'createdAt', 'criadoEm', 'CriadoEm'),
        updatedAt: pick(j, 'updatedAt', 'concluidoEm', 'ConcluidoEm', 'criadoEm', 'CriadoEm'),
    });

    const normalizeRow = (r) => ({
        id: pick(r, 'id', '_id') || `${pick(r, 'experimentId')}-${pick(r, 'updatedAt')}`,
        experimentId: pick(r, 'experimentId') || '—',
        analysisId: pick(r, 'analysisId') || '—',
        valido: Boolean(pick(r, 'valido')),
        motivos: Array.isArray(pick(r, 'motivos')) ? pick(r, 'motivos') : [],
        updatedAt: pick(r, 'updatedAt'),
    });

    const loadValidationRows = async () => {
        const params = { limit: 5000 };
        if (filterAnalysis.trim()) params.analysisId = filterAnalysis.trim();
        if (filterValido === 'true') params.valido = true;
        if (filterValido === 'false') params.valido = false;
        const data = await listValidationExperiments(params);
        const mapped = Array.isArray(data) ? data.map(normalizeRow) : [];
        setRows(mapped);
        setTablePage(0);
    };

    const loadAll = async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setErro('');
        }
        try {
            const [st, list] = await Promise.all([
                getValidationStatus(),
                listValidationJobs(),
            ]);
            const mappedJobs = Array.isArray(list) ? list.map(normalizeJob) : [];
            setStatus(st || { hasValidation: false, hasActive: false });
            setJobs(mappedJobs);

            const running = mappedJobs.find((j) => j.status === 'running' || j.status === 'pauseRequested' || j.status === 'paused' || j.status === 'em_processamento');
            if (running?.id) {
                const l = await getValidationLogs(running.id);
                setLogs(Array.isArray(l) ? l : []);
            } else {
                setLogs([]);
            }

            await loadValidationRows();
        } catch (e) {
            if (!silent) {
                setErro(e?.response?.data?.error || e?.message || 'Erro ao carregar dados de validação');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    useEffect(() => {
        loadValidationRows().catch(() => {});
    }, [filterValido, filterAnalysis]);

    useEffect(() => {
        if (!status?.hasActive) {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            return undefined;
        }

        const tenant = currentTenant();
        if (!tenant) return undefined;

        const ws = new WebSocket(`${wsBaseUrl()}/ws/${tenant}`);
        wsRef.current = ws;

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data || '{}');
                const msgJobId = pick(msg, 'jobId');
                if (!msgJobId) return;

                const msgStatus = pick(msg, 'status') || 'running';
                const msgTotal = Number(pick(msg, 'total') || 0);
                const msgProcessados = Number(pick(msg, 'processados') || 0);
                const msgText = pick(msg, 'msg');

                setJobs((prev) => prev.map((job) => {
                    if (job.id !== msgJobId) return job;
                    return {
                        ...job,
                        status: msgStatus,
                        total: msgTotal > 0 ? msgTotal : job.total,
                        processados: msgProcessados,
                    };
                }));

                if (msgText) {
                    setLogs((prev) => [
                        {
                            id: `ws-${Date.now()}`,
                            createdAt: new Date().toISOString(),
                            level: 'info',
                            message: msgText,
                        },
                        ...prev,
                    ].slice(0, 300));
                }

                const stillActive = msgStatus === 'running' || msgStatus === 'pauseRequested';
                setStatus((prev) => ({ ...prev, hasActive: stillActive }));
                if (!stillActive) {
                    loadAll({ silent: true });
                }
            } catch {
                // ignore
            }
        };

        return () => {
            ws.close();
            if (wsRef.current === ws) wsRef.current = null;
        };
    }, [status?.hasActive]);

    const handleStartGlobal = async () => {
        setErro('');
        setGlobalLoading(true);
        try {
            await startValidationGlobal({});
            await loadAll({ silent: true });
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao iniciar validação global');
        } finally {
            setGlobalLoading(false);
        }
    };

    const handlePause = async () => {
        if (!activeJob?.id) return;
        setJobActionLoading(true);
        try {
            await pauseValidation(activeJob.id);
            await loadAll({ silent: true });
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao pausar validação');
        } finally {
            setJobActionLoading(false);
        }
    };

    const handleResume = async () => {
        if (!activeJob?.id) return;
        setJobActionLoading(true);
        try {
            await resumeValidation(activeJob.id);
            await loadAll({ silent: true });
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao retomar validação');
        } finally {
            setJobActionLoading(false);
        }
    };

    const handleDelete = async (jobId) => {
        if (!jobId) return;
        if (!window.confirm('Confirma excluir esse job de validação?')) return;
        setDeleteLoadingId(jobId);
        try {
            await deleteValidation(jobId);
            await loadAll({ silent: true });
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao excluir validação');
        } finally {
            setDeleteLoadingId('');
        }
    };

    const handleValidateOne = async () => {
        if (!oneId.trim()) return;
        setOneLoading(true);
        setErro('');
        try {
            await validateOne({ amostraId: oneId.trim() });
            await loadValidationRows();
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro na validação unitária');
        } finally {
            setOneLoading(false);
        }
    };

    const handleValidateAnalysis = async () => {
        if (!analysisInput.trim()) return;
        setAnalysisLoading(true);
        setErro('');
        try {
            const payload = analysisMode === 'amostraId'
                ? { amostraId: analysisInput.trim() }
                : { analysisId: analysisInput.trim() };
            await validateAnalysis(payload);
            await loadValidationRows();
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro na validação por análise');
        } finally {
            setAnalysisLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>
            {loading && <Alert severity="info">Carregando dados da validação…</Alert>}
            {erro && <Alert severity="error">{erro}</Alert>}
            {compatibilityMode === 'legacy-validar' && (
                <Alert severity="warning">
                    Backend em modo legado (/validar). Recursos avançados (pausar, retomar, excluir e tabela nova) podem não estar disponíveis até atualizar o pipe-ia.
                </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Lucide.ChartBar color={theme.palette.primary.main} />
                    <Typography variant="subtitle1" fontWeight={700}>Validação Global (Dump)</Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Executa validação assíncrona em todos os snapshots do dump, sem sobrecarga no data_analise.
                </Typography>

                {activeJob && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight={700}>
                                Job {activeJob.id} - {STATUS_MAP[activeJob.status]?.label || activeJob.status}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handlePause}
                                    disabled={jobActionLoading || activeJob.status !== 'running' || compatibilityMode === 'legacy-validar'}
                                >
                                    Pausar
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleResume}
                                    disabled={jobActionLoading || (activeJob.status !== 'paused' && activeJob.status !== 'pauseRequested') || compatibilityMode === 'legacy-validar'}
                                >
                                    Retomar
                                </Button>
                            </Box>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <LinearProgress variant="determinate" value={progressPct} />
                            <Typography variant="caption" color="text.secondary">
                                {activeJob.processados || 0}/{activeJob.total || 0} ({progressPct}%)
                            </Typography>
                        </Box>
                    </Paper>
                )}

                <Button
                    variant="contained"
                    startIcon={globalLoading ? <CircularProgress size={16} color="inherit" /> : <Lucide.Play />}
                    onClick={handleStartGlobal}
                    disabled={globalLoading || status?.hasActive}
                >
                    {globalLoading ? 'Iniciando…' : 'Validar Global'}
                </Button>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Lucide.FlaskConical color={theme.palette.primary.main} />
                    <Typography variant="subtitle1" fontWeight={700}>Validação Unitária e por Análise</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                    <TextField
                        label="Experimento/Amostra"
                        size="small"
                        value={oneId}
                        onChange={(e) => setOneId(e.target.value)}
                        sx={{ minWidth: 260 }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={oneLoading ? <CircularProgress size={14} /> : <Lucide.Search />}
                        onClick={handleValidateOne}
                        disabled={oneLoading || !oneId.trim()}
                    >
                        Validar Unitário
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        label="Tipo"
                        size="small"
                        select
                        value={analysisMode}
                        onChange={(e) => setAnalysisMode(e.target.value)}
                        sx={{ minWidth: 170 }}
                    >
                        <MenuItem value="analysisId">Por AnalysisId</MenuItem>
                        <MenuItem value="amostraId">Por Amostra</MenuItem>
                    </TextField>
                    <TextField
                        label={analysisMode === 'amostraId' ? 'Amostra' : 'AnalysisId'}
                        size="small"
                        value={analysisInput}
                        onChange={(e) => setAnalysisInput(e.target.value)}
                        sx={{ minWidth: 260 }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={analysisLoading ? <CircularProgress size={14} /> : <Lucide.Search />}
                        onClick={handleValidateAnalysis}
                        disabled={analysisLoading || !analysisInput.trim()}
                    >
                        Validar Análise
                    </Button>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} mb={2}>Tabela da Aba Validation</Typography>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <TextField
                        label="Filtro AnalysisId"
                        size="small"
                        value={filterAnalysis}
                        onChange={(e) => setFilterAnalysis(e.target.value)}
                    />
                    <TextField
                        label="Filtro Validação"
                        size="small"
                        select
                        value={filterValido}
                        onChange={(e) => setFilterValido(e.target.value)}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="todos">Todos</MenuItem>
                        <MenuItem value="true">Válidos</MenuItem>
                        <MenuItem value="false">Inválidos</MenuItem>
                    </TextField>
                    <Button variant="outlined" onClick={() => loadValidationRows()}>Atualizar Tabela</Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <StatBox label="Registros" value={totalRows} />
                    <StatBox label="Válidos" value={totalValidos} color="success.main" />
                    <StatBox label="Inválidos" value={totalInvalidos} color="error.main" />
                </Box>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Experimento/Amostra</TableCell>
                                <TableCell>AnalysisId</TableCell>
                                <TableCell>Validação</TableCell>
                                <TableCell>Motivos</TableCell>
                                <TableCell>Atualizado em</TableCell>
                                <TableCell>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            Sem registros para os filtros atuais.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {pagedRows.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.experimentId}</TableCell>
                                    <TableCell>{row.analysisId}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.valido ? 'Válido' : 'Inválido'}
                                            color={row.valido ? 'success' : 'error'}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 360 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {row.motivos?.length ? row.motivos.join(' | ') : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{fmtDateTime(row.updatedAt)}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Lucide.ExternalLink size={18} />}
                                            onClick={() => {
                                                const href = graphPathForExperiment(row.experimentId);
                                                if (!href) return;
                                                navigate(href, { state: { experiment: fallbackExperimentForGraph(row) } });
                                            }}
                                            disabled={!graphPathForExperiment(row.experimentId)}
                                        >
                                            Ver gráfico
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={rows.length}
                        page={tablePage}
                        onPageChange={(_, newPage) => setTablePage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setTablePage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Linhas por página"
                    />
                </TableContainer>
            </Paper>

            <Box>
                <Typography variant="subtitle1" fontWeight={700} mb={2}>Histórico de Jobs</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {jobs.map((job) => {
                        const st = STATUS_MAP[job.status] ?? STATUS_MAP.completed;
                        return (
                            <Card key={job.id} variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                        <Typography variant="body1" fontWeight={700}>
                                            Job {job.id}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Chip label={st.label} color={st.color} size="small" />
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(job.id)}
                                                disabled={deleteLoadingId === job.id || job.status === 'running' || compatibilityMode === 'legacy-validar'}
                                            >
                                                {deleteLoadingId === job.id ? 'Excluindo…' : 'Excluir'}
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Processados</Typography>
                                            <Typography variant="body2" fontWeight={700}>{job.processados}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="success.main">Válidos</Typography>
                                            <Typography variant="body2" fontWeight={700} color="success.main">{job.validos}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="error.main">Inválidos</Typography>
                                            <Typography variant="body2" fontWeight={700} color="error.main">{job.invalidos}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Criado em</Typography>
                                            <Typography variant="body2">{fmtDateTime(job.createdAt)}</Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                    {jobs.length === 0 && (
                        <Typography variant="body2" color="text.secondary">Nenhum job de validação ainda.</Typography>
                    )}
                </Box>
            </Box>

            {activeJob && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Logs do Job Ativo</Typography>
                    <Box sx={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {logs.length === 0 && (
                            <Typography variant="caption" color="text.secondary">Sem logs ainda.</Typography>
                        )}
                        {logs.map((l, idx) => (
                            <Typography key={`${l.id || l.ID || idx}`} variant="caption" sx={{ fontFamily: 'monospace' }}>
                                [{fmtDateTime(l.createdAt || l.CreatedAt)}] {l.level || l.Level}: {l.message || l.Message}
                            </Typography>
                        ))}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
